import { useState } from 'react';
import { Competence } from '../../../../shared/types/config';
import configService from '../../../../shared/services/configService';

interface UseCompetenceConfigParams {
  setError: (msg: string | null) => void;
  setSuccess: (msg: string | null) => void;
  loadAllData: () => Promise<void>;
}

export function useCompetenceConfig({
  setError,
  setSuccess,
  loadAllData,
}: UseCompetenceConfigParams) {
  const [competences, setCompetences] = useState<Competence[]>([]);
  const [searchCompetence, setSearchCompetence] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'normal' | 'specialite'>('all');
  const [filterService, setFilterService] = useState<number | 'all'>('all');
  const [showCompetenceModal, setShowCompetenceModal] = useState(false);
  const [editingCompetence, setEditingCompetence] = useState<Competence | null>(null);
  const [competenceForm, setCompetenceForm] = useState<Omit<Competence, 'id'>>({
    nom: '',
    is_specialite: false,
    id_service: null,
    description: '',
    ordre: undefined,
  });

  const handleAddCompetence = () => {
    setEditingCompetence(null);
    setCompetenceForm({
      nom: '',
      is_specialite: false,
      id_service: null,
      description: '',
      ordre: undefined,
    });
    setShowCompetenceModal(true);
  };

  const handleEditCompetence = (competence: Competence) => {
    setEditingCompetence(competence);
    setCompetenceForm({
      nom: competence.nom,
      is_specialite: competence.is_specialite,
      id_service: competence.id_service,
      description: competence.description || '',
      ordre: competence.ordre,
    });
    setShowCompetenceModal(true);
  };

  const handleSaveCompetence = async () => {
    try {
      setError(null);
      let result;
      if (editingCompetence?.id) {
        result = await configService.updateCompetence(editingCompetence.id, competenceForm);
      } else {
        result = await configService.createCompetence(competenceForm);
      }
      if (result.success) {
        setSuccess(editingCompetence ? 'Compétence modifiée avec succès' : 'Compétence ajoutée avec succès');
        setShowCompetenceModal(false);
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteCompetence = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette compétence ?')) return;
    try {
      setError(null);
      const result = await configService.deleteCompetence(id);
      if (result.success) {
        setSuccess('Compétence supprimée avec succès');
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  return {
    competences,
    setCompetences,
    searchCompetence,
    setSearchCompetence,
    filterType,
    setFilterType,
    filterService,
    setFilterService,
    showCompetenceModal,
    setShowCompetenceModal,
    editingCompetence,
    competenceForm,
    setCompetenceForm,
    handleAddCompetence,
    handleEditCompetence,
    handleSaveCompetence,
    handleDeleteCompetence,
  };
}
